import { Controller } from "@hotwired/stimulus"
import { setWritable } from "plan_storage"

// Single-tab editing lock. The plan lives in ONE localStorage blob shared by
// every tab of the same browser; two tabs editing the same plan clobber each
// other (last write wins). This controller elects ONE active writer tab.
//
// Mechanism: a localStorage "heartbeat lock" — universally supported, and
// `storage` events broadcast ownership changes across tabs instantly. The
// owner re-stamps the lock every HEARTBEAT_MS; a lock older than STALE_MS
// (owner crashed / closed) is free to claim. A NON-owner tab is made
// non-writable via setWritable(false) — so it can neither edit (overlay) nor
// PERSIST (plan_storage refuses the write) — which is the actual data-loss
// guarantee. "Use this tab instead" hands the lock over: the previous owner's
// storage listener sees the new owner and blocks itself.
//
// Handles: refresh (same per-tab id in sessionStorage reclaims), a crashed
// owner (stale-expiry), background-tab timer throttling (re-evaluates on
// visibilitychange→visible and on every storage event), and take-over.
const LOCK_KEY     = "pcp_tab_lock"
const TABID_KEY    = "pcp_tab_id"
const HEARTBEAT_MS = 3000
const STALE_MS     = 9000   // 3 missed heartbeats ⇒ owner is gone

export default class extends Controller {
  static targets = ["overlay"]

  connect() {
    // PCPDesk desktop host: each window is pinned to its OWN plan file (see
    // plan_store_bridge / window.__PCP_STORAGE__) and the app enforces one client
    // per window, so nothing is shared to clobber. The tab lock keys on a single
    // global localStorage entry (not per-plan), so in the shared WebView2 profile
    // it would falsely pause a second window that's editing a DIFFERENT client.
    // Disable it entirely in the desktop host.
    if (window.__PCP_DESKTOP__) {
      this._disabled = true
      setWritable(true)
      if (this.hasOverlayTarget) this.overlayTarget.hidden = true
      document.documentElement.classList.remove("pcp-tab-blocked")
      return
    }

    // Per-tab id that survives a refresh (sessionStorage is per-tab, per-tab
    // lifetime) but is unique across separate tabs.
    this.tabId = sessionStorage.getItem(TABID_KEY)
    if (!this.tabId) {
      this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      try { sessionStorage.setItem(TABID_KEY, this.tabId) } catch (_) {}
    }
    this._owner = null
    this._onStorage = (e) => { if (e.key === LOCK_KEY) this._evaluate() }
    window.addEventListener("storage", this._onStorage)
    // The lock FOLLOWS FOCUS: whichever tab the user is actively looking at
    // becomes the writer, so their edits ALWAYS save. Becoming visible or
    // focused CLAIMS the lock (the other tab then blocks itself). This is the
    // key fix over a first-come lock, which could silently block the tab the
    // user was actually editing in. Safe because a blocked tab keeps its plan
    // current from cross-tab storage events, so it isn't stale when it takes over.
    this._onVisible = () => { if (document.visibilityState === "visible") this._claim() }
    document.addEventListener("visibilitychange", this._onVisible)
    this._onFocus = () => this._claim()
    window.addEventListener("focus", this._onFocus)
    this._onPagehide = () => this._release()
    window.addEventListener("pagehide", this._onPagehide)
    // On boot: a FOREGROUND tab claims immediately; a background-opened tab only
    // evaluates (claims if free/stale) so it doesn't steal from the active tab.
    if (document.visibilityState === "visible" && (!document.hasFocus || document.hasFocus())) this._claim()
    else this._evaluate()
    this._timer = setInterval(() => this._heartbeat(), HEARTBEAT_MS)
  }

  disconnect() {
    if (this._disabled) { setWritable(true); return }
    window.removeEventListener("storage", this._onStorage)
    document.removeEventListener("visibilitychange", this._onVisible)
    window.removeEventListener("focus", this._onFocus)
    window.removeEventListener("pagehide", this._onPagehide)
    clearInterval(this._timer)
    this._release()
    setWritable(true)   // don't leave the gate shut if we tear down
  }

  // Become the writer NOW (the user focused this tab). Other tabs see the new
  // owner via their storage listener and block themselves.
  _claim() {
    this._stamp()
    this._setOwner(true)
  }

  _readLock() {
    try { return JSON.parse(localStorage.getItem(LOCK_KEY) || "null") } catch (_) { return null }
  }
  _stamp() {
    try { localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId: this.tabId, ts: Date.now() })) } catch (_) {}
  }
  _release() {
    const lk = this._readLock()
    if (lk && lk.tabId === this.tabId) { try { localStorage.removeItem(LOCK_KEY) } catch (_) {} }
  }

  // Decide whether THIS tab owns the lock; claim it if free / stale / ours.
  _evaluate() {
    const lk = this._readLock()
    const free = !lk || (Date.now() - (lk.ts || 0) > STALE_MS)
    if (free || lk.tabId === this.tabId) {
      this._stamp()
      this._setOwner(true)
    } else {
      this._setOwner(false)
    }
  }

  _heartbeat() {
    if (this._owner) this._stamp()     // keep the lock warm
    else this._evaluate()              // reclaim if the owner vanished
  }

  _setOwner(owner) {
    if (this._owner === owner) return
    this._owner = owner
    setWritable(owner)
    if (this.hasOverlayTarget) this.overlayTarget.hidden = owner
    document.documentElement.classList.toggle("pcp-tab-blocked", !owner)
  }

  // "Use this tab instead" — steal the lock. The previous owner's storage
  // listener sees the new owner id and blocks itself. Reload so this tab
  // re-reads the freshest plan from storage before the user edits.
  takeOver() {
    this._stamp()
    this._setOwner(true)
    location.reload()
  }
}
